import React, { useState, useRef, useEffect, useCallback } from 'react';
import { flushSync } from 'react-dom';
import {
  ChevronLeft,
  ChevronRight,
  ZoomIn,
  X,
  Plus,
  Sparkles,
  Download,
  MapPin,
  Calendar,
  GraduationCap,
  Hash,
  UserRound,
  UsersRound,
  GripVertical,
  SquarePen,
} from 'lucide-react';
import { Header } from '../Common';
import Input from '@ajat/design-system/Input';
import Button from '@ajat/design-system/Button';
import SidePanel from '@ajat/design-system/SidePanel';
import Slider from '@ajat/design-system/Slider';
import { LargeLabelSelector } from '@ajat/design-system/Configurator';
import Spinner from '../../../UI/Spinner';
import {
  init3D,
  drawLogo,
  MIN_LOGO_SIZE,
  MAX_LOGO_SIZE,
  DEFAULT_LOGO_SIZE,
  LOGO_SIZE_STEP,
  CANVAS_WIDTH,
  CANVAS_HEIGHT,
} from './logoCanvas';
import { NAME_FONTS, DEFAULT_NAME_FONT, getNameFont, loadLogoFonts } from './logoFonts';
import { getRecraftImage } from './recraftApi';
import brandPlaceholder from '../../../../assets/cls-dk/logo-generator/example-3.jpg';
import './LogoGenerator.css';
import MobileFocusModal from '@ajat/design-system/MobileFocus';
import useMobileFocus from '@ajat/design-system/hooks/useMobileFocus';

/* ══════════════════════════════════════════════════════════════════════════
 * CLS-DK — AI Logo Generator (studietur)
 *
 * Native React port of the standalone Vue logo generator (logo.clseifert.com).
 * Generates a monoline B/W city-skyline logo via Recraft AI, then composites
 * it onto an 1800×2400 canvas with school name + class + student names.
 * ══════════════════════════════════════════════════════════════════════════ */

/**
 * Nearest scrollable ancestor. Found by computed style rather than by the
 * SidePanel's own class so this doesn't break if the DS markup changes.
 */
const scrollableAncestor = (el) => {
  let node = el?.parentElement;
  while (node) {
    const { overflowY } = window.getComputedStyle(node);
    if (
      (overflowY === 'auto' || overflowY === 'scroll') &&
      node.scrollHeight > node.clientHeight
    ) {
      return node;
    }
    node = node.parentElement;
  }
  return null;
};

const PAGE_TITLE = 'Lav et logo';
const PAGE_SUBTITLE = 'Til jeres studietur';
const PAGE_DESCRIPTION =
  'Her kan du hurtigt og nemt generere et unikt logo til jeres studieturstrøjer. Leg med designet og få flere forslag, indtil du finder et, der passer til jer.';

/* Where a field lands when the focus card closes: its `.logo-gen__block`, 20px
 * below the sticky preview — the logo generator's counterpart of the product
 * pages' PRODUCT_FOCUS_REVEAL. */
const LOGO_GEN_FOCUS_REVEAL = Object.freeze({
  section: '.logo-gen__block',
  sticky: '.logo-gen__preview',
  gap: 20,
  align: 'top',
});

/* Sidebar text fields that edit inside the mobile focus card. Merged at render
 * with their value/setter (and the city field's validation error). */
const FOCUS_FIELDS = {
  city: { label: 'Destinationens by', icon: <MapPin size={20} strokeWidth={1.5} />, maxLength: 100 },
  year: { label: 'Årstal', icon: <Calendar size={20} strokeWidth={1.5} />, maxLength: 100 },
  schoolName: { label: 'Skole', icon: <GraduationCap size={20} strokeWidth={1.5} />, maxLength: 100 },
  className: { label: 'Klasse', icon: <Hash size={20} strokeWidth={1.5} />, maxLength: 100 },
};

const LogoGenerator = () => {
  // ── Form state ──
  const [city, setCity] = useState('');
  const [year, setYear] = useState('');
  const [schoolName, setSchoolName] = useState('');
  const [className, setClassName] = useState('');
  const [students, setStudents] = useState(['']);
  const [nameFont, setNameFont] = useState(DEFAULT_NAME_FONT);
  const [logoSize, setLogoSize] = useState(DEFAULT_LOGO_SIZE);

  // ── Students side panel ──
  const [studentsPanelOpen, setStudentsPanelOpen] = useState(false);
  const [dragIndex, setDragIndex] = useState(null); // row enabled for dragging
  const dragItemRef = useRef(null); // index currently being dragged

  // ── Canvas / image state ──
  const canvasRef = useRef(null);
  const brandRef = useRef(null);
  const imagesRef = useRef([]); // HTMLImageElement[]
  const [imageCount, setImageCount] = useState(0);
  const [index, setIndex] = useState(-1);
  const [ready, setReady] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [zoomed, setZoomed] = useState(false);

  // Rendered width of the canvas. It is height-constrained (`width: auto` +
  // `max-height: 100%`), so its laid-out width is not its intrinsic width and
  // CSS alone cannot size the controls below it to match — measure instead.
  const [canvasWidth, setCanvasWidth] = useState(null);

  useEffect(() => {
    const el = canvasRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(([entry]) => {
      setCanvasWidth(entry.contentRect.width);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  /* ── Mobile: editing happens in the shared focus card ─────────────────────
   * Tapping a field opens MobileFocusModal — a card above the keyboard — so the
   * preview keeps its full height and nothing has to be scrolled out of the way.
   * ──────────────────────────────────────────────────────────────────────── */
  const {
    isMobile,
    focusField,
    activeFocusContainerRef,
    openFocusMode,
    closeFocusMode,
  } = useMobileFocus({ reveal: LOGO_GEN_FOCUS_REVEAL });

  const focusFields = {
    city: {
      ...FOCUS_FIELDS.city,
      placeholder: FOCUS_FIELDS.city.label,
      value: city,
      setValue: setCity,
      error: error ? 'Indtast venligst en by for at generere et logo' : '',
    },
    year: { ...FOCUS_FIELDS.year, placeholder: FOCUS_FIELDS.year.label, value: year, setValue: setYear },
    schoolName: {
      ...FOCUS_FIELDS.schoolName,
      placeholder: FOCUS_FIELDS.schoolName.label,
      value: schoolName,
      setValue: setSchoolName,
    },
    className: {
      ...FOCUS_FIELDS.className,
      placeholder: FOCUS_FIELDS.className.label,
      value: className,
      setValue: setClassName,
    },
  };
  const activeFocusData = focusField ? focusFields[focusField] : focusFields.city;

  /* Desktop renders the plain Input; mobile wraps it in a focus trigger — the
   * inner Input is readOnly and pointer-events:none, the wrapper opens the card. */
  const renderField = (key) => {
    const field = focusFields[key];
    const inputProps = {
      label: field.label,
      icon: field.icon,
      value: field.value,
      onChange: (e) => field.setValue(e.target.value),
      maxLength: field.maxLength,
      error: field.error || '',
    };

    if (!isMobile) return <Input {...inputProps} />;

    return (
      <div
        id={`mf-trigger-${key}`}
        className="mf__trigger"
        onClick={(e) => { e.preventDefault(); openFocusMode(key); }}
      >
        <Input {...inputProps} readOnly tabIndex={-1} />
      </div>
    );
  };

  // Latest form values for redraw (avoid stale closures inside async flows)
  const formRef = useRef({ schoolName, className, students, nameFont, logoSize });
  formRef.current = { schoolName, className, students, nameFont, logoSize };

  // ── Redraw helper ──
  const redraw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !ready) return;
    const { schoolName: s, className: c, students: st, nameFont: f, logoSize: z } = formRef.current;
    const image = index >= 0 ? imagesRef.current[index] : null;
    const font = getNameFont(f);
    drawLogo(canvas, null, image, s, c, st, {
      fontStack: font.stack,
      fontScale: font.scale,
      logoSize: z,
    });
  }, [index, ready]);

  // ── Mount: load fonts + brand placeholder, then init canvas ──
  useEffect(() => {
    let cancelled = false;

    const loadBrand = () =>
      new Promise((resolve) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => resolve(img);
        img.onerror = () => resolve(null);
        img.src = brandPlaceholder;
      });

    (async () => {
      // Every selectable face must resolve before the first draw — Canvas2D
      // silently falls back to a default face for an unloaded family.
      const [, brand] = await Promise.all([loadLogoFonts(), loadBrand()]);
      if (cancelled) return;
      brandRef.current = brand;
      const canvas = canvasRef.current;
      if (canvas) {
        const font = getNameFont(DEFAULT_NAME_FONT);
        init3D(canvas, brand, { fontStack: font.stack, fontScale: font.scale });
      }
      setReady(true);
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  // ── Form refs for scrolling/focusing on validation failure ──
  const cityInputRef = useRef(null);

  // ── Live redraw on text changes (school / class / students) ──
  useEffect(() => {
    if (!ready) return;
    // Only redraw the composed logo once at least one image exists.
    // Before that the canvas shows the brand placeholder.
    if (index >= 0) redraw();
  }, [schoolName, className, students, nameFont, logoSize, index, ready, redraw]);

  // ── Generate via Recraft ──
  const handleGenerate = async () => {
    if (!city.trim()) {
      setError(true);
      // Smoothly scroll to the city input field for a seamless UX (no programmatic autofocus)
      if (cityInputRef.current) {
        cityInputRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }
    setError(false);
    setLoading(true);
    try {
      const url = await getRecraftImage(city.trim(), year.trim());
      if (!url) {
        setLoading(false);
        return;
      }
      const image = new Image();
      image.crossOrigin = 'anonymous';
      image.src = url;
      await image.decode();
      const newIndex = imagesRef.current.length;
      imagesRef.current.push(image);
      setImageCount(imagesRef.current.length);
      setIndex(newIndex);
    } catch (e) {
      console.error('Logo generation failed', e);
    } finally {
      setLoading(false);
    }
  };

  // ── Variant navigation (arrows + the thumbnail strip under the canvas) ──
  const prevImage = () => setIndex((i) => Math.max(0, i - 1));
  const nextImage = () => setIndex((i) => Math.min(imagesRef.current.length - 1, i + 1));

  const isPrev = index > 0;
  const isNext = imageCount > 0 && index < imageCount - 1;
  const hasImage = index >= 0;

  // ── Students helpers ──
  const studentCount = students.filter((s) => s.trim()).length;
  const listRef = useRef(null);

  const updateStudent = (i, value) => {
    setStudents((prev) => prev.map((s, idx) => (idx === i ? value : s)));
  };
  const addStudent = () => {
    flushSync(() => {
      setStudents((prev) => [...prev, '']);
    });

    // Programmatic focus synchronous with click user gesture bypasses mobile virtual keyboard block
    if (listRef.current) {
      const inputs = listRef.current.querySelectorAll('.logo-gen__panel-input input');
      if (inputs.length > 0) {
        inputs[inputs.length - 1].focus();
      }
    }

    // Pin the panel to the bottom so the new row AND the "Tilføj" button below
    // it stay in view.
    const scroller = scrollableAncestor(listRef.current);
    if (!scroller) return;
    const toBottom = () => scroller.scrollTo({ top: scroller.scrollHeight, behavior: 'smooth' });
    toBottom();

    // One scroll is not enough on iOS: focus() makes Safari run its own
    // scroll-into-view, and the panel itself shrinks as the keyboard animates
    // in (SidePanel pins to the visual viewport), so the bottom keeps moving.
    // Re-run on each viewport change and settle on the last one.
    const viewport = window.visualViewport;
    let timer = setTimeout(toBottom, 350);
    const onViewportChange = () => {
      clearTimeout(timer);
      timer = setTimeout(toBottom, 150);
    };
    viewport?.addEventListener('resize', onViewportChange);
    // Stop listening once the keyboard has settled; the pending scroll still runs.
    setTimeout(() => viewport?.removeEventListener('resize', onViewportChange), 1500);
  };
  const removeStudent = (i) => {
    setStudents((prev) => (prev.length === 1 ? [''] : prev.filter((_, idx) => idx !== i)));
  };

  // ── Drag-to-reorder (order impacts final canvas composition) ──
  const reorderStudents = (from, to) => {
    setStudents((prev) => {
      if (from === to || from == null || to == null) return prev;
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
  };
  const handleDragStart = (i) => {
    dragItemRef.current = i;
  };
  const handleDragEnter = (i) => {
    const from = dragItemRef.current;
    if (from == null || from === i) return;
    reorderStudents(from, i);
    dragItemRef.current = i;
  };
  const handleDragEnd = () => {
    dragItemRef.current = null;
    setDragIndex(null);
  };

  // ── Touch reorder events (for mobile devices) ──
  const handleTouchStart = (e, i) => {
    setDragIndex(i);
    dragItemRef.current = i;
  };
  const handleTouchMove = (e) => {
    const touch = e.touches[0];
    const element = document.elementFromPoint(touch.clientX, touch.clientY);
    if (!element) return;
    const row = element.closest('.logo-gen__panel-row');
    if (row) {
      const indexAttr = row.getAttribute('data-index');
      if (indexAttr !== null) {
        const targetIndex = parseInt(indexAttr, 10);
        const from = dragItemRef.current;
        if (from !== null && from !== targetIndex) {
          reorderStudents(from, targetIndex);
          dragItemRef.current = targetIndex;
        }
      }
    }
  };
  const handleTouchEnd = () => {
    dragItemRef.current = null;
    setDragIndex(null);
  };

  // ── Download PDF ──
  const handleDownload = async () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const { jsPDF } = await import('jspdf');
    const doc = new jsPDF('p', 'mm');

    // Fit the canvas to the page by its own ratio — the canvas is no longer 3:4,
    // and hardcoded mm would stretch it.
    const margin = 3;
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const imgWidth = pageWidth - margin * 2;
    const imgHeight = imgWidth * (CANVAS_HEIGHT / CANVAS_WIDTH);
    const offsetY = Math.max(margin, (pageHeight - imgHeight) / 2);

    doc.addImage(canvas, 'JPEG', margin, offsetY, imgWidth, imgHeight);
    doc.save('logo.pdf');
  };

  // Rendered in the controls row normally, and as an overlay while zoomed —
  // the controls are hidden then, and the close affordance has to survive.
  const zoomButton = (extraClass = '') => (
    <Button
      size="secondary"
      variant="gray"
      iconOnly
      iconLeft={zoomed ? <X size={20} strokeWidth={1.5} /> : <ZoomIn size={20} strokeWidth={1.5} />}
      onClick={() => setZoomed((z) => !z)}
      aria-label={zoomed ? 'Luk zoom' : 'Zoom'}
      className={`logo-gen__zoom ${extraClass}`.trim()}
    />
  );

  return (
    <div className="logo-gen">
      <Header showTopBar={false} hideOnScroll={false} schoolSelectorLockScroll={false} />

      <main className="logo-gen__main">
        {/* ── Canvas preview ── */}
        <section
          className={`logo-gen__preview ${zoomed ? 'logo-gen__preview--zoomed' : ''}`}
        >
          <div className="logo-gen__stage">
            {!zoomed && (
              <Button
                size="secondary"
                variant="gray"
                iconOnly
                iconLeft={<ChevronLeft size={20} strokeWidth={1.5} />}
                onClick={prevImage}
                aria-label="Forrige logo"
                className={`logo-gen__nav logo-gen__nav--prev ${isPrev ? '' : 'logo-gen__nav--hidden'}`}
              />
            )}

            <div className="logo-gen__canvas-wrap">
              {loading && (
                <div className="logo-gen__loader">
                  <Spinner />
                </div>
              )}
              <canvas
                ref={canvasRef}
                width={CANVAS_WIDTH}
                height={CANVAS_HEIGHT}
                className="logo-gen__canvas"
                style={{ aspectRatio: `${CANVAS_WIDTH} / ${CANVAS_HEIGHT}` }}
              />
            </div>

            {!zoomed && (
              <Button
                size="secondary"
                variant="gray"
                iconOnly
                iconLeft={<ChevronRight size={20} strokeWidth={1.5} />}
                onClick={nextImage}
                aria-label="Næste logo"
                className={`logo-gen__nav logo-gen__nav--next ${isNext ? '' : 'logo-gen__nav--hidden'}`}
              />
            )}
          </div>

          {/* Scale + generated variants, directly under the logotype.
              Width tracks the canvas, which is height-constrained — see canvasWidth. */}
          {!zoomed && hasImage && (
            <div
              className="logo-gen__controls"
              style={canvasWidth ? { width: `${canvasWidth}px` } : undefined}
            >
              <Slider
                label="Logo størrelse"
                value={logoSize}
                min={MIN_LOGO_SIZE}
                max={MAX_LOGO_SIZE}
                step={LOGO_SIZE_STEP}
                onChange={setLogoSize}
                formatValue={(v) => `${v}%`}
              />

              {/* Only worth showing once there is a choice to make */}
              {imageCount > 1 && (
                <div className="logo-gen__thumbs" role="listbox" aria-label="Genererede logoer">
                  {Array.from({ length: imageCount }, (_, i) => (
                    <button
                      key={i}
                      type="button"
                      role="option"
                      aria-selected={i === index}
                      aria-label={`Logo ${i + 1}`}
                      className={`logo-gen__thumb ${i === index ? 'logo-gen__thumb--active' : ''}`}
                      onClick={() => setIndex(i)}
                    >
                      <img
                        src={imagesRef.current[i]?.src}
                        alt=""
                        className="logo-gen__thumb-img"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Pinned to the preview's bottom-right corner so the slider above
              keeps the canvas's full width to itself. */}
          {(zoomed || hasImage) && zoomButton(zoomed ? 'logo-gen__zoom--overlay' : '')}
        </section>

        {/* ── Form sidebar ── */}
        <aside className="logo-gen__sidebar">
          <div className="logo-gen__intro">
            <h1 className="logo-gen__title typo-h1">{PAGE_TITLE}</h1>
            <h3 className="logo-gen__subtitle typo-h3">{PAGE_SUBTITLE}</h3>
            <p className="logo-gen__desc typo-body">{PAGE_DESCRIPTION}</p>
          </div>

          {/* Rejseoplysninger */}
          <div className="logo-gen__block">
            <h2 className="logo-gen__block-title typo-h4">Rejseoplysninger</h2>
            <div ref={cityInputRef}>{renderField('city')}</div>
            {renderField('year')}
          </div>

          {/* Skole oplysninger */}
          <div className="logo-gen__block">
            <h2 className="logo-gen__block-title typo-h4">Skole oplysninger</h2>
            {renderField('schoolName')}
            {renderField('className')}
          </div>

          {/* Tilføj elever */}
          <div className="logo-gen__block">
            <h2 className="logo-gen__block-title typo-h4">Elever</h2>
            <button
              type="button"
              className={`logo-gen__students-trigger ${studentCount > 0 ? 'logo-gen__students-trigger--filled' : ''}`}
              onClick={() => setStudentsPanelOpen(true)}
              aria-label="Tilføj elever"
            >
              <span className="logo-gen__students-trigger-icon logo-gen__students-trigger-icon--left">
                <UsersRound size={20} strokeWidth={2} />
              </span>
              <span className="logo-gen__students-trigger-label typo-input">
                {studentCount > 0
                  ? `${studentCount} ${studentCount === 1 ? 'elev' : 'elever'}`
                  : 'Tilføj elever'}
              </span>
              <span className="logo-gen__students-trigger-icon logo-gen__students-trigger-icon--right">
                {studentCount > 0 ? (
                  <SquarePen size={20} strokeWidth={1.5} />
                ) : (
                  <Plus size={20} strokeWidth={1.5} />
                )}
              </span>
            </button>
          </div>

          {/* Skrifttype til navne */}
          <div className="logo-gen__block">
            <h2 className="logo-gen__block-title typo-h4">Skrifttype til navne</h2>
            <div className="logo-gen__font-row">
              {NAME_FONTS.map((font) => (
                <LargeLabelSelector
                  key={font.id}
                  selected={font.id === nameFont}
                  onClick={() => setNameFont(font.id)}
                >
                  {/* Each option previews its own face; the sample size is
                      bumped in LogoGenerator.css, scoped to this picker. */}
                  <span className="logo-gen__font-sample" style={{ fontFamily: font.stack }}>
                    {font.label}
                  </span>
                </LargeLabelSelector>
              ))}
            </div>
          </div>

        </aside>
      </main>

      {/* ── Sticky action bar ── */}
      <div className={`logo-gen__actions ${focusField ? 'logo-gen__actions--hidden' : ''}`.trim()}>
        <div className="logo-gen__actions-inner">
          {hasImage && (
            <Button
              size="primary"
              variant="black"
              iconLeft={<Download size={20} strokeWidth={1.5} />}
              onClick={handleDownload}
              className="logo-gen__action-btn"
            >
              Download PDF
            </Button>
          )}
          <Button
            size="primary"
            variant="main"
            iconLeft={<Sparkles size={20} strokeWidth={1.5} />}
            loading={loading}
            onClick={handleGenerate}
            className="logo-gen__action-btn"
          >
            {hasImage ? 'Lav nyt logo' : 'Generér logo'}
          </Button>
        </div>
      </div>

      {/* ── Students side panel (add / reorder) ── */}
      <SidePanel
        isOpen={studentsPanelOpen}
        onClose={() => setStudentsPanelOpen(false)}
        title="Elever"
      >
        <div className="logo-gen__panel">
          <p className="logo-gen__panel-hint typo-body-sm">
            Tilføj elevernes navne. Træk i håndtaget for at ændre rækkefølgen — den
            afspejles i det færdige logo.
          </p>

          <div className="logo-gen__panel-list" ref={listRef}>
            {students.map((student, i) => (
              <div
                className={`logo-gen__panel-row ${dragIndex === i ? 'logo-gen__panel-row--dragging' : ''}`}
                key={i}
                data-index={i}
                draggable={dragIndex === i}
                onDragStart={() => handleDragStart(i)}
                onDragEnter={() => handleDragEnter(i)}
                onDragOver={(e) => e.preventDefault()}
                onDragEnd={handleDragEnd}
              >
                <div className="logo-gen__panel-input">
                  <Input
                    label={`Elev ${i + 1}`}
                    icon={<UserRound size={20} strokeWidth={1.5} />}
                    value={student}
                    onChange={(e) => updateStudent(i, e.target.value)}
                    maxLength={100}
                  />
                </div>
                <button
                  type="button"
                  className="logo-gen__panel-remove"
                  onClick={() => removeStudent(i)}
                  aria-label="Fjern elev"
                >
                  <X size={18} strokeWidth={1.5} />
                </button>
                <button
                  type="button"
                  className="logo-gen__panel-handle"
                  onMouseDown={() => setDragIndex(i)}
                  onTouchStart={(e) => handleTouchStart(e, i)}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleTouchEnd}
                  aria-label="Træk for at omarrangere"
                >
                  <GripVertical size={20} strokeWidth={1.5} />
                </button>
              </div>
            ))}
          </div>

          <Button
            size="primary"
            variant="black"
            iconLeft={<Plus size={20} strokeWidth={1.5} />}
            onClick={addStudent}
            className="logo-gen__panel-add"
          >
            Tilføj
          </Button>
        </div>
      </SidePanel>

      {/* Mobile focus card — shared with the product pages */}
      {isMobile && (
        <MobileFocusModal
          focusField={focusField}
          activeFocusData={activeFocusData}
          closeFocusMode={closeFocusMode}
          containerRef={activeFocusContainerRef}
        />
      )}
    </div>
  );
};

export default LogoGenerator;
